import {
  RIG_FRAC_FOCUS_BIN_KEY_LIMIT,
  RIG_FRAC_FOCUS_BIN_VALUE_MAX_LENGTH
} from "constants/rigFrac.constants";
import type { RigFracFocusBin } from "types/charts/requests";

import type { IColorPalette } from "models/colorPalette";

import { FocusColorResolution } from "./buildFocusColors";
import { NA_MATCH_KEY } from "./operatorKeys";

export interface FocusColorGateParams {
  resolution: FocusColorResolution;
  /**
   * The rig/frac attribute this specific layer or chart is scoped to right
   * now — a layer's Color By field, or a chart's own group-by dimension.
   * Generic `string` (not RigFracFocusAttribute) because a layer can be
   * coloured by attributes ("locationType", ...) a focus can never match,
   * exactly like `colorByField` on rigFracColorAssignment.
   */
  layerAttribute: Maybe<string>;
}

/**
 * Whether this layer's/chart's own field is the one dimension the active or
 * pending focus would colour, independent of whether real colours are ready
 * yet. Shared by shouldApplyFocusColors (which additionally requires
 * `active`) and shouldHoldForPendingFocusColors (which requires `pending`),
 * so the two can never disagree about which surfaces are even in scope for a
 * given focus.
 */
export function isFocusAttributeMatch(params: FocusColorGateParams): boolean {
  return (
    params.resolution.attribute != null &&
    params.layerAttribute === params.resolution.attribute
  );
}

/**
 * The single gate for "does this layer/chart take real focus colours right
 * now?", shared by the map adapter and the layer panel so they can never
 * disagree. A never-set layer already resolves to "operator" upstream, so
 * this one equality covers both "explicitly Operator" and "left at default"
 * for the operator triggers, and generalises to every other trigger.
 */
export function shouldApplyFocusColors(params: FocusColorGateParams): boolean {
  return params.resolution.state === "active" && isFocusAttributeMatch(params);
}

/**
 * The single gate for "should this layer/chart hold what it already shows,
 * rather than repaint at all?" — true only while a trigger matching this
 * surface's own attribute is pending. This is what stops the Plasma-flash
 * bug: a pending resolver must never be treated as inactive (which would
 * repaint the base palette) nor as active (no real colours exist yet).
 */
export function shouldHoldForPendingFocusColors(params: FocusColorGateParams): boolean {
  return params.resolution.state === "pending" && isFocusAttributeMatch(params);
}

/** The focus colours a surface hands to buildRigFracColorAssignment. */
export interface FocusPaintColors {
  colors: ReadonlyMap<string, string>;
  reservedColors: ReadonlySet<string>;
  basePalette?: Maybe<IColorPalette>;
  // Optional so existing literals stay valid; always set from a resolution.
  binOfKey?: ReadonlyMap<string, string>;
  binColors?: ReadonlyMap<string, string>;
  hasOthers?: boolean;
  othersColor?: string;
}

/**
 * The single decision every rig/frac colouring surface makes per paint, shared
 * by the map adapter, the layer panel and the charts so they cannot diverge:
 *
 * - a matching focus is active: use its colours
 * - a matching focus is pending: reuse the colours this attribute last resolved
 *   to (`held`, from the shared focus colour hold store), so the surface can
 *   still repaint everything else. An override edit, a refreshed value domain
 *   or a style reload during the window then lands instead of being dropped:
 *   the hold covers the focus colours only, never the whole paint.
 * - a matching focus is pending with nothing yet held: no focus colours, which
 *   paints the ordinary base palette. This is the cold case (nothing has ever
 *   resolved for this attribute), where there is no earlier paint to preserve
 *   and no reason to look different from a surface that has no focus at all.
 * - otherwise: colour as if no focus existed
 *
 * `held` is keyed by attribute by the store, so a held answer can never be read
 * against the wrong dimension.
 */
export function resolveFocusPaint(
  params: FocusColorGateParams & { held: Maybe<FocusPaintColors> }
): Maybe<FocusPaintColors> {
  const { resolution, layerAttribute, held } = params;
  const gateParams = { resolution, layerAttribute };

  if (shouldHoldForPendingFocusColors(gateParams)) {
    return held;
  }

  if (!shouldApplyFocusColors(gateParams)) {
    return undefined;
  }

  return {
    colors: resolution.colors,
    reservedColors: resolution.reservedColors,
    basePalette: resolution.basePalette,
    binOfKey: resolution.binOfKey,
    binColors: resolution.binColors,
    hasOthers: resolution.hasOthers,
    othersColor: resolution.othersColor
  };
}

export interface RigFracFocusBinsSelection {
  focusBins: RigFracFocusBin[];
  foldOthers: boolean;
}

/**
 * Whether a rig/frac chart sends focus bins for this paint. It needs a named
 * bin (n/a alone does not count), or everything would fold into Others, and it
 * must stay under chart-svc's key and length caps, or the request is a 400.
 * Either way the chart keeps its own labels instead.
 */
export function sendsFocusBins(paint: Maybe<FocusPaintColors>): boolean {
  const binOfKey = paint?.binOfKey;
  if (!binOfKey || binOfKey.size > RIG_FRAC_FOCUS_BIN_KEY_LIMIT) {
    return false;
  }
  let hasNamedBin = false;
  for (const [key, label] of binOfKey) {
    if (
      key.length > RIG_FRAC_FOCUS_BIN_VALUE_MAX_LENGTH ||
      label.length > RIG_FRAC_FOCUS_BIN_VALUE_MAX_LENGTH
    ) {
      return false;
    }
    if (key !== NA_MATCH_KEY) hasNamedBin = true;
  }
  return hasNamedBin;
}

/**
 * The bins a rig/frac chart maps its labels onto, or undefined for none.
 * Folding into Others needs the focus to have an Others bin: only then did Max
 * Bins cut values off.
 */
export function focusBinsFromPaint(
  paint: Maybe<FocusPaintColors>
): Maybe<RigFracFocusBinsSelection> {
  if (!paint?.binOfKey || !sendsFocusBins(paint)) {
    return undefined;
  }

  const keysByLabel = new Map<string, string[]>();
  paint.binOfKey.forEach((label, key) => {
    const keys = keysByLabel.get(label);
    if (keys) keys.push(key);
    else keysByLabel.set(label, [key]);
  });
  // Sorted so the same bins always serialize the same way.
  const focusBins = [...keysByLabel]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, keys]) => ({ label, keys: keys.sort() }));

  return { focusBins, foldOthers: !!paint.hasOthers };
}
